import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PatientDetailResponse } from '../../../../../core/models/patient/patient-detail.model';
import { GlassAssessmentMetadata } from '../../../../../core/models/assessment/assessment.model';
import {
  WoundStatus,
  WoundAssessmentsDto,
  WoundAssessmentRiskCategoryDto,
} from '../../../../../core/models/wound/wound.model';
import {
  GlassTreatmentRecommendationKey,
  getGlassTreatmentRecommendation,
} from '../../../../../core/constants/glass-scoring';

@Component({
  selector: 'app-patient-overview',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './patient-overview.component.html',
  styleUrls: ['./patient-overview.component.scss'],
})
export class PatientOverviewComponent implements OnInit, OnChanges {
  @Input() patient!: PatientDetailResponse;
  @Input() scores: any = {};
  @Input() latestAssessment: WoundAssessmentsDto | null = null;
  @Input() showBackButton = false;
  @Input() backRoute: string[] | null = null;
  @Output() backClick = new EventEmitter<void>();

  @Input() selectedWoundParentId: string | null = null;
  @Input() selectedWoundStatus: number | null = null;
  @Input() canCreateFollowUp: boolean | null = null;
  @Input() isWoundActive: boolean = true;

  showNewWoundButton = false;
  showFollowUpButton = false;
  currentWoundId: string | null = null;

  constructor(
    private router: Router,
    private location: Location,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.updateButtons();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['canCreateFollowUp'] ||
      changes['selectedWoundStatus'] ||
      changes['isWoundActive']
    ) {
      this.updateButtons();
    }
  }

  private updateButtons(): void {
    this.showNewWoundButton =
      this.router.url.includes('/patient/detail') &&
      !this.router.url.includes('/create');
    const woundMatch = this.router.url.match(
      /\/wound\/([-a-fA-F0-9]+)\/assessment/
    );
    if (woundMatch && woundMatch[1]) {
      if (this.canCreateFollowUp !== null) {
        this.showFollowUpButton = this.canCreateFollowUp && this.isWoundActive;
      } else {
        this.showFollowUpButton =
          this.selectedWoundStatus === WoundStatus.TreatmentCompleted &&
          this.isWoundActive;
      }
      this.currentWoundId = woundMatch[1];
    }
  }

  onBackClick(): void {
    if (this.backRoute) {
      this.router.navigate(this.backRoute);
    } else {
      this.location.back();
    }
    this.backClick.emit();
  }

  onNewWoundClick(): void {
    if (this.patient?.id || this.patient?.recordId) {
      this.router.navigate(
        ['/patient', 'edit', this.patient.id || this.patient.recordId],
        {
          queryParams: { startStep: 3, hideStepper: true, hasWound: true },
        }
      );
    }
  }

  onNoWoundClick(): void {
    if (this.patient?.id || this.patient?.recordId) {
      this.router.navigate(
        ['/patient', 'edit', this.patient.id || this.patient.recordId],
        {
          queryParams: { startStep: 3, hideStepper: true, hasWound: false },
        }
      );
    }
  }

  onNewFollowUpClick(): void {
    if ((this.patient?.id || this.patient?.recordId) && this.currentWoundId) {
      const parentWoundId = this.selectedWoundParentId || this.currentWoundId;

      this.router.navigate(
        ['/patient', 'edit', this.patient.id || this.patient.recordId],
        {
          queryParams: {
            startStep: 3,
            hideStepper: true,
            parentWoundId: parentWoundId,
            woundType: 1,
          },
        }
      );
    }
  }

  getPatientInitial(): string {
    if (this.patient?.name) {
      return this.patient.name.charAt(0).toUpperCase();
    }
    return '?';
  }

  getFullName(): string {
    const parts = [this.patient?.name, this.patient?.surname].filter(Boolean);
    return parts.join(' ') || 'Unknown Patient';
  }

  getGenderKey(gender: number | undefined): string {
    if (gender === 2) return 'male';
    if (gender === 3) return 'female';
    if (gender === 1) return 'other';
    return '';
  }

  getQuestionsForRisk(templateId: string): any[] {
    if (!this.latestAssessment) return [];
    return (
      this.latestAssessment.woundAssessmentQuestions?.filter(
        (q) => q.assessmentTemplateId === templateId
      ) || []
    );
  }

  getDisplayedRiskName(risk: WoundAssessmentRiskCategoryDto): string {
    if (this.isWifiRisk(risk)) {
      return this.getWifiRiskLevelText(risk);
    }

    if (this.isGlassRisk(risk)) {
      return '';
    }

    if (!this.isIWGDFRisk(risk)) {
      return risk.riskCategoryName;
    }

    const riskLevelKeys: { [score: number]: string } = {
      0: 'patientCreate.woundAssessment.iwgdfRiskLevels.no',
      1: 'patientCreate.woundAssessment.iwgdfRiskLevels.slightly',
      2: 'patientCreate.woundAssessment.iwgdfRiskLevels.moderate',
      3: 'patientCreate.woundAssessment.iwgdfRiskLevels.severe',
    };

    return this.translate.instant(riskLevelKeys[risk.totalScore] || riskLevelKeys[3]);
  }

  getIWGDFFollowUpText(risk: WoundAssessmentRiskCategoryDto): string {
    const followUpKeys: { [score: number]: string } = {
      0: 'patientCreate.woundAssessment.iwgdfFollowUp.score0',
      1: 'patientCreate.woundAssessment.iwgdfFollowUp.score1',
      2: 'patientCreate.woundAssessment.iwgdfFollowUp.score2',
      3: 'patientCreate.woundAssessment.iwgdfFollowUp.score3',
    };

    return this.translate.instant(followUpKeys[risk.totalScore] || followUpKeys[3]);
  }

  isIWGDFRisk(risk: WoundAssessmentRiskCategoryDto): boolean {
    return (risk.assessmentTemplateCode || '').toLowerCase().includes('iwgdf');
  }

  isWifiRisk(risk: WoundAssessmentRiskCategoryDto): boolean {
    return (risk.assessmentTemplateCode || '').toLowerCase().includes('wifi');
  }

  isGlassRisk(risk: WoundAssessmentRiskCategoryDto): boolean {
    return (risk.assessmentTemplateCode || '').toLowerCase().includes('glass');
  }

  getDisplayAssessmentCode(text: string | undefined): string {
    return this.formatWifiDisplayText(text);
  }

  getDisplayAssessmentName(text: string | undefined): string {
    return this.formatWifiDisplayText(text);
  }

  getDisplayQuestionText(text: string | undefined): string {
    return this.formatWifiDisplayText(text);
  }

  getDisplayAnswerText(text: string | undefined): string {
    return this.formatWifiDisplayText(text, true);
  }

  getWifiStageText(risk: WoundAssessmentRiskCategoryDto): string {
    return this.translate.instant(
      `patientCreate.woundAssessment.wifi.stages.${risk.totalScore}`,
    );
  }

  getWifiRiskLevelText(risk: WoundAssessmentRiskCategoryDto): string {
    const keys: Record<number, string> = {
      1: 'patientCreate.woundAssessment.wifi.riskLevels.veryLow',
      2: 'patientCreate.woundAssessment.wifi.riskLevels.low',
      3: 'patientCreate.woundAssessment.wifi.riskLevels.moderate',
      4: 'patientCreate.woundAssessment.wifi.riskLevels.high',
    };

    return this.translate.instant(keys[risk.totalScore] || keys[4]);
  }

  getWifiMajorAmputationRate(risk: WoundAssessmentRiskCategoryDto): string {
    const rates: Record<number, string> = {
      1: '0.75%',
      2: '5.90%',
      3: '8.40%',
      4: '25%',
    };

    return rates[risk.totalScore] || rates[4];
  }

  getGlassStageText(risk: WoundAssessmentRiskCategoryDto): string {
    return this.translate.instant(
      `patientCreate.woundAssessment.glass.stages.${risk.totalScore}`,
    );
  }

  getGlassRecommendationLabel(risk: WoundAssessmentRiskCategoryDto): string {
    const recommendationKey = this.getGlassRecommendationKey(risk);
    if (!recommendationKey) {
      return risk.description || risk.riskCategoryName;
    }

    return this.translate.instant(
      `patientCreate.woundAssessment.glass.recommendations.${recommendationKey}`,
    );
  }

  getGlassBreakdown(
    risk: WoundAssessmentRiskCategoryDto,
  ): { fp: string; ip: string } | null {
    const metadata = this.getGlassMetadata(risk);
    if (metadata?.stageAnswers?.FP || metadata?.stageAnswers?.IP) {
      return {
        fp: metadata.stageAnswers.FP || '—',
        ip: metadata.stageAnswers.IP || '—',
      };
    }

    const answers = this.getQuestionsForRisk(risk.assessmentTemplateId)
      .flatMap((question) => question.woundAssessmentAnswers || [])
      .map((answer) => answer.answerText || '');

    if (!answers.length) {
      return null;
    }

    return {
      fp: answers.find((answerText) => answerText.startsWith('FP')) || '—',
      ip: answers.find((answerText) => answerText.startsWith('IP')) || '—',
    };
  }

  getGlassSelectedVesselsText(risk: WoundAssessmentRiskCategoryDto): string | null {
    const metadata = this.getGlassMetadata(risk);
    if (!metadata?.selectedVessels?.length) {
      return null;
    }

    return metadata.selectedVessels.map((vessel) => vessel.label).join(', ');
  }

  private getGlassRecommendationKey(
    risk: WoundAssessmentRiskCategoryDto,
  ): GlassTreatmentRecommendationKey | null {
    const wifiRisk = this.latestAssessment?.woundAssessmentRiskCategories?.find((item) =>
      this.isWifiRisk(item),
    );

    if (!wifiRisk) {
      return null;
    }

    return getGlassTreatmentRecommendation(risk.totalScore, wifiRisk.totalScore);
  }

  private getGlassMetadata(
    risk: WoundAssessmentRiskCategoryDto,
  ): GlassAssessmentMetadata | null {
    if (!risk.metadata) {
      return null;
    }

    try {
      return JSON.parse(risk.metadata) as GlassAssessmentMetadata;
    } catch {
      return null;
    }
  }

  getWifiBreakdown(
    risk: WoundAssessmentRiskCategoryDto,
  ): { wound: number; ischemia: number; footInfection: number } | null {
    if (!this.latestAssessment) {
      return null;
    }

    const wagnerRisk = this.latestAssessment.woundAssessmentRiskCategories?.find(
      (item) =>
        (item.assessmentTemplateCode || '').toLowerCase().includes('maggitwagner'),
    );
    const iwgdfRisk = this.latestAssessment.woundAssessmentRiskCategories?.find(
      (item) => (item.assessmentTemplateCode || '').toLowerCase().includes('iwgdf'),
    );
    const wifiAnswer = this.getQuestionsForRisk(risk.assessmentTemplateId)
      .flatMap((question) => question.woundAssessmentAnswers || [])
      .map((answer) => answer.answerText || '')
      .find((answerText) => this.getWifiIschemiaFromAnswerText(answerText) !== null);

    if (!wagnerRisk || !iwgdfRisk || !wifiAnswer) {
      return null;
    }

    const ischemia = this.getWifiIschemiaFromAnswerText(wifiAnswer);

    if (ischemia === null) {
      return null;
    }

    return {
      wound: this.mapWagnerToWifiWoundScore(wagnerRisk.totalScore),
      ischemia,
      footInfection: Math.max(0, Math.min(3, iwgdfRisk.totalScore)),
    };
  }

  private mapWagnerToWifiWoundScore(wagnerScore: number): number {
    if (wagnerScore <= 0) return 0;
    if (wagnerScore === 1) return 1;
    if (wagnerScore <= 4) return 2;
    return 3;
  }

  private getWifiIschemiaFromAnswerText(answerText: string): number | null {
    const match = answerText.match(/^I([0-3])\b/i);
    if (match) {
      return Number(match[1]);
    }

    if (answerText.includes('>= 0.80')) return 0;
    if (answerText.includes('0.60 - 0.79')) return 1;
    if (answerText.includes('0.40 - 0.59')) return 2;
    if (answerText.includes('<= 0.39')) return 3;

    return null;
  }

  private formatWifiDisplayText(
    text: string | undefined,
    removeIschemiaPrefix = false,
  ): string {
    if (!text) {
      return '';
    }

    let formatted = text
      .replace(/\bWIFI\b/g, 'WIfI')
      .replace(/\bWifi\b/g, 'WIfI')
      .replace(/^WIfI\s*\((Wound|Yara|Wond)\)\s*(Template|Skalası|Sjabloon)$/i, 'WIfI')
      .replace(/^WifiWound Template$/i, 'WIfI')
      .replace(/^WIfI Ischemia Classification\?$/i, 'WIfI Ischemia Classification');

    if (removeIschemiaPrefix) {
      formatted = formatted.replace(/^I[0-3]\s*-\s*/i, '');
      formatted = formatted.split('|')[0].trim();
    }

    return formatted;
  }
}
